import { useState, useEffect, useRef, useCallback, useContext } from "react";
import { api } from "../config/api";
import { util } from "../config/util";
import AlertContext from "../contexts/AlertContext";

export interface ExcelTaskItem {
  id: number;
  status: "pending" | "processing" | "done" | "failed" | "cancelled";
  service_type: string;
  file_name: string | null;
  file_url: string | null;
  file_size: number | null;
  error_message: string | null;
  created_at: string | null;
  updated_at: string | null;
  // 진행률·예상 시간(.NET → Django 폴러 → 목록 API). 옛 서버/완료 작업에는 없을 수 있다.
  progress?: number | null;
  rows_done?: number | null;
  rows_total?: number | null;
  elapsed_seconds?: number | null;
  estimated_total_seconds?: number | null;
  estimated_remaining_seconds?: number | null;
}

const POLL_INTERVAL = 5000; // 5초
// 상태 조회가 이만큼 연달아 실패하면 "확인하지 못하고 있다"고 한 번만 알린다.
const POLL_FAIL_ALERT_THRESHOLD = 5;

// 서버가 준 오류 문구가 있으면 그것을, 없으면 기본 문구를 쓴다.
const getErrorMessage = (err: any, fallback: string) => {
  const data = err?.response?.data;
  const serverMessage =
    (typeof data === "string" && data) || data?.message || data?.error || data?.detail;
  return serverMessage ? `${fallback}\n${serverMessage}` : fallback;
};

export function useExcelTaskPolling() {
  const alertContext = useContext(AlertContext);
  const [tasks, setTasks] = useState<ExcelTaskItem[]>([]);
  // 상태 조회 연속 실패 횟수와 알림 여부 (도배 방지)
  const pollFailCountRef = useRef(0);
  const pollFailAlertedRef = useRef(false);
  const tasksRef = useRef<ExcelTaskItem[]>([]);
  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeTaskIdsRef = useRef<Set<number>>(new Set());
  const downloadedIdsRef = useRef<Set<number>>(new Set());
  // 컴포넌트 마운트 상태 추적 — unmount 후 setState 방지
  const mountedRef = useRef(true);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, []);

  // tasksRef를 항상 최신 상태로 유지
  tasksRef.current = tasks;

  const hasActiveTasks = tasks.some(
    (t) => t.status === "pending" || t.status === "processing"
  );

  // task 추가 (실행 직후 호출)
  const addTasks = useCallback((newTasks: { task_id: number; file_name: string }[]) => {
    const taskItems: ExcelTaskItem[] = newTasks.map((t) => ({
      id: t.task_id,
      status: "pending" as const,
      service_type: "",
      file_name: t.file_name,
      file_url: null,
      file_size: null,
      error_message: null,
      created_at: new Date().toISOString(),
      updated_at: null,
    }));

    setTasks((prev) => [...taskItems, ...prev]);
    newTasks.forEach((t) => activeTaskIdsRef.current.add(t.task_id));
  }, []);

  // 완료된 task 자동 다운로드 (useEffect로 분리 — setTasks 밖에서 실행)
  useEffect(() => {
    tasks.forEach((task, idx) => {
      if (
        task.status === "done" &&
        task.file_url &&
        activeTaskIdsRef.current.has(task.id) &&
        !downloadedIdsRef.current.has(task.id)
      ) {
        downloadedIdsRef.current.add(task.id);
        activeTaskIdsRef.current.delete(task.id);
        // 다운로드 간 간격을 두어 브라우저 throttling 방지
        setTimeout(() => {
          const downloadUrl = task.file_url!.replace(/\+/g, "%2B");
          util.downloadFile(downloadUrl);
        }, idx * 500);
      } else if (task.status === "failed" || task.status === "cancelled") {
        activeTaskIdsRef.current.delete(task.id);
      }
    });
  }, [tasks]);

  // 폴링: GET /excel/tasks/ 한 번으로 전체 갱신 (네트워크 요청 최소화)
  const pollTasks = useCallback(async () => {
    const currentTasks = tasksRef.current;
    const activeTasks = currentTasks.filter(
      (t) => t.status === "pending" || t.status === "processing"
    );
    if (activeTasks.length === 0) return;

    try {
      const res = await api.getExcelTasks();
      const serverTasks: ExcelTaskItem[] = res.data?.tasks || [];
      const serverMap = new Map(serverTasks.map((t) => [t.id, t]));

      setTasks((prev) =>
        prev.map((task) => {
          const updated = serverMap.get(task.id);
          if (updated) {
            return { ...task, ...updated };
          }
          return task;
        })
      );
      pollFailCountRef.current = 0;
      pollFailAlertedRef.current = false;
    } catch (err) {
      console.error("Excel task polling error:", err);
      pollFailCountRef.current += 1;
      if (
        pollFailCountRef.current >= POLL_FAIL_ALERT_THRESHOLD &&
        !pollFailAlertedRef.current &&
        mountedRef.current
      ) {
        pollFailAlertedRef.current = true;
        alertContext.showAlert(
          "처리 상태를 확인하지 못하고 있습니다.\n페이지를 새로고침해 주세요."
        );
      }
    }
  }, [alertContext]);

  // setTimeout 체이닝으로 폴링 (이전 요청 완료 후 다음 요청)
  useEffect(() => {
    if (!hasActiveTasks) {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }

    let cancelled = false;

    const schedule = () => {
      pollingRef.current = setTimeout(async () => {
        if (cancelled) return;
        await pollTasks();
        if (!cancelled) schedule();
      }, POLL_INTERVAL);
    };

    schedule();

    return () => {
      cancelled = true;
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [hasActiveTasks, pollTasks]);

  // 페이지 로드 시 기존 내역 복원 — 일시적 500/네트워크 에러 대비 retry
  const loadExistingTasks = useCallback(async (retriesLeft = 2): Promise<void> => {
    if (!mountedRef.current) return;
    try {
      const res = await api.getExcelTasks();
      if (!mountedRef.current) return;
      if (res.data?.tasks) {
        setTasks(res.data.tasks);
      }
    } catch (err) {
      console.error(`Failed to load existing tasks (retries left: ${retriesLeft}):`, err);
      if (retriesLeft > 0 && mountedRef.current) {
        // 지수 백오프: 2초 → 4초 (총 3번 시도)
        const delay = (3 - retriesLeft) * 2000;
        if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        retryTimerRef.current = setTimeout(() => loadExistingTasks(retriesLeft - 1), delay);
      }
    }
  }, []);

  // task 취소
  const cancelTask = useCallback(async (taskId: number) => {
    try {
      await api.cancelExcelTask(taskId);
      setTasks((prev) =>
        prev.map((t) =>
          t.id === taskId
            ? { ...t, status: "cancelled" as const, error_message: "사용자가 취소했습니다." }
            : t
        )
      );
      activeTaskIdsRef.current.delete(taskId);
    } catch (err) {
      console.error("Failed to cancel task:", err);
      alertContext.showAlert(getErrorMessage(err, "작업을 취소하지 못했습니다."));
    }
  }, [alertContext]);

  // task 재시도 (실패/취소 상태)
  const retryTask = useCallback(async (taskId: number) => {
    try {
      const res = await api.retryExcelTask(taskId);
      const { task_id, file_name } = res.data;
      // 기존 task 제거 + 새 task 추가
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
      addTasks([{ task_id, file_name }]);
    } catch (err) {
      console.error("Failed to retry task:", err);
      alertContext.showAlert(getErrorMessage(err, "작업을 다시 시도하지 못했습니다."));
    }
  }, [addTasks, alertContext]);

  // task 삭제 (완료/실패/취소 상태만)
  const deleteTask = useCallback(async (taskId: number) => {
    try {
      await api.deleteExcelTask(taskId);
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
      activeTaskIdsRef.current.delete(taskId);
      downloadedIdsRef.current.delete(taskId);
    } catch (err) {
      console.error("Failed to delete task:", err);
      alertContext.showAlert(getErrorMessage(err, "작업을 삭제하지 못했습니다."));
    }
  }, [alertContext]);

  // 활성 task 전체 취소
  const cancelAllTasks = useCallback(async () => {
    try {
      await api.cancelAllExcelTasks();
      setTasks((prev) =>
        prev.map((t) =>
          t.status === "pending" || t.status === "processing"
            ? { ...t, status: "cancelled" as const, error_message: "사용자가 취소했습니다." }
            : t
        )
      );
      activeTaskIdsRef.current.clear();
    } catch (err) {
      console.error("Failed to cancel all tasks:", err);
      alertContext.showAlert(getErrorMessage(err, "작업을 모두 취소하지 못했습니다."));
    }
  }, [alertContext]);

  // task 목록 비우기 (DB에서도 삭제). statusFilter: 'all' | 'done' | 'failed'
  const clearTasks = useCallback(async (statusFilter?: string) => {
    try {
      await api.clearAllExcelTasks(statusFilter === 'all' ? undefined : statusFilter);
    } catch (err) {
      // 서버에서 못 지웠는데 화면만 비우면 새로고침 때 다시 나타나므로 화면도 그대로 둔다.
      console.error("Failed to clear tasks:", err);
      alertContext.showAlert(getErrorMessage(err, "목록을 비우지 못했습니다."));
      return;
    }
    if (!statusFilter || statusFilter === 'all') {
      setTasks([]);
      activeTaskIdsRef.current.clear();
      downloadedIdsRef.current.clear();
    } else if (statusFilter === 'done') {
      setTasks((prev) => prev.filter((t) => t.status !== 'done'));
    } else if (statusFilter === 'failed') {
      setTasks((prev) => prev.filter((t) => t.status !== 'failed' && t.status !== 'cancelled'));
    }
  }, [alertContext]);

  return {
    tasks,
    addTasks,
    loadExistingTasks,
    clearTasks,
    cancelTask,
    cancelAllTasks,
    deleteTask,
    retryTask,
    hasActiveTasks,
  };
}
